import { useMutation, useSuspenseQuery } from '@apollo/client';
import { produce } from 'immer';
import React, { useMemo, useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import {
  createUserGroupMutation,
  getTenantUserGroupsQuery,
  tenantUserGroupsFragment,
} from './operations';

type Row = {
  id: string;
  name: string;
};

const CreateUserGroupDialog: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();

  const { isDialogOpen, toggleDialog } = useDialog();
  const [newGroupName, setNewGroupName] = useState('');

  const [createUserGroup] = useMutation(createUserGroupMutation, {
    onError: () => toast.error(t('tenantAdmin.userGroups.createFailed')),
    optimisticResponse: () => ({
      insertUserGroupsOne: {
        __typename: 'UserGroups' as const,
        id: 'optimistic',
        name: newGroupName,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: tenantUserGroupsFragment,
          key: {
            id: tenantId,
          },
        },
        (existing) => {
          const newGroup = data?.insertUserGroupsOne;
          if (!existing || !newGroup) {
            return existing;
          }

          return produce(existing, (draft) => {
            draft.user_groups.push(newGroup);
          });
        },
      );
    },
  });

  const handleCreateUserGroup = async () => {
    await createUserGroup({
      variables: {
        name: newGroupName,
      },
    });

    setNewGroupName('');
    toggleDialog();
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={toggleDialog} modal>
      <DialogTrigger asChild>
        <Button size="sm">
          <Icon name="Plus" size="auto" />
          {t('tenantAdmin.userGroups.createUserGroup')}
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {t('tenantAdmin.userGroups.newUserGroupTitle')}
          </DialogTitle>
        </DialogHeader>
        <Input
          placeholder={t('tenantAdmin.userGroups.groupNamePlaceholder')}
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={toggleDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreateUserGroup} disabled={!newGroupName}>
            {t('tenantAdmin.common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserGroupsListContent: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { t } = useTranslation();

  const cols = useMemo(
    () =>
      new ColDefBuilder<Row>()
        .detailsLink((r) => r.id, t('tenantAdmin.common.detailsColumn'))
        .defaultSortable('name', t('tenantAdmin.common.nameColumn'))
        .build(),
    [t],
  );

  const { data } = useSuspenseQuery(getTenantUserGroupsQuery, {
    variables: {
      tenantId,
    },
  });

  return (
    <DataTable
      enableGlobalSearch
      columns={cols}
      data={data.tenantsByPk?.user_groups ?? []}
    />
  );
};

export const UserGroupsListPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <SettingsPageFrame
      size="wide"
      title={t('tenantAdmin.userGroups.title')}
      actions={<CreateUserGroupDialog />}
    >
      <UserGroupsListContent />
    </SettingsPageFrame>
  );
};
